import {api} from "../api";

import {Combobox} from "../ui/combobox";
import {FormUtil} from "../forms/formUtil";
import {GroupDropdown} from "../ui/groupDropdown";
import {permissionForSaveButton} from "../securityHelper";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {
	formatDate,
	formatDuration,
	formatTime,
	getCurrentISOOffset,
	isBeforeDay,
	normalizeTimeInput,
	parseDateTimeZone,
	parseDuration,
	parseIntegerNumber,
	parseISOZonedDateTime,
	Temporal
} from "@/shared/utils/datefunctions";
import {BreakTimeRecordingMode, DateTimeChangeableByUserDayRangeMode, RecordedEntryStatus,} from "@/shared/timrTypes";
import {initCustomFields, updateCustomFields} from "@/shared/customFieldHelper";
import {CustomFieldDefinition, CustomFieldOption, DateTimeChangeableResult, Group, ReportRecord, User} from "@/shared/timr";
import {SecurityStatus} from "@/shared/securityStatus";
import {Notify} from "@/shared/ui/notify";

export enum DialogMode { Create = "Create", Edit = "Edit", Show = "Show", Duplicate = "Duplicate" }

export interface RecordDialogConfig {
	dialogId: string;
	title?: {
		[key in keyof typeof DialogMode]: string
	};
	size?: BootstrapModal.ModalSize;
	users: User[];
	userSelectionEnabled: boolean;
	customFieldDefinitions: CustomFieldDefinition[];
	defaultStatusNumber: number;
	allowedUserIds: number[];
	isGroupLeaderForThemself: boolean;
	dateTimeChangeableByUserDayRangeUntil: Temporal.PlainDate | null;
	dateTimeChangeableByUserDayRangeMode: DateTimeChangeableByUserDayRangeMode;
	preventCloseIfLightboxOpen?: boolean;
}

export type DisplayedUser = Pick<User, "id" | "fullname">;

export abstract class RecordDialog<T extends ReportRecord> {

	public static EVENT_RECORD_EDITED = "recordEdited";
	public static EVENT_RECORD_CREATED = "recordCreated";
	public static EVENT_RECORD_DELETED = "recordDeleted";
	public static EVENT_REQUEST_CREATED = "requestCreated";

	public static readonly DEFAULT_DURATION = 30 * 60 * 1000;

	public config: RecordDialogConfig;

	protected dom: JQuery;
	protected dialog: BootstrapModal.RecordModal;
	protected dialogMode = DialogMode.Create;
	protected durationChanged = false;
	protected hasCustomFieldCheckboxChangeListener: boolean[] = [];
	private lastRecordEnd: Temporal.ZonedDateTime | null;
	private groupDropdown: GroupDropdown | null;
	private selectedGroupId: number;
	private userCombobox: Combobox<DisplayedUser> | null;
	private customFieldComboboxes: Combobox<CustomFieldOption>[] = [];
	private originalRecording: T;

	constructor(config: RecordDialogConfig) {
		this.config = config;
		this.config.size = this.config.size || "modal-xl";

		this.dom = $(this.config.dialogId);
		this.initDialog();
		this.initCalendars();
		this.createTimeShiftButtons();
		this.initCustomFields();
		this.initGroupDropdown();
		this.initUserCombobox();
		this.initDescriptionAutoExpand();
	}

	public create(lastRecord?: Partial<T>, createAnother = false) {
		this.originalRecording = null;
		if (createAnother) {
			this.dialog.resetLoadingButtons();
		} else {
			this.showButtons(true, false, true);
		}
		this.setDialogMode(DialogMode.Create);
		this.removeAllFeedback();
		this.disableFields(false);
		this.durationChanged = false;
		this.lastRecordEnd = parseISOZonedDateTime(lastRecord?.end);
		this.dom.find(".id").val("");
		this.dom.find(".version").val("0");
		this.dom.find(".changed").val("true");
		this.dom.find(".groupRow").toggle(this.config.userSelectionEnabled);
		if (this.config.userSelectionEnabled) {
			this.reloadUserList();
		}
		this.updateUserCombobox(this.config.users, lastRecord?.userId);
		this.userCombobox?.setDisabled(false);
		const startDate = this.lastRecordEnd ?? Temporal.Now.zonedDateTimeISO();
		this.setStartDateTimeFields(startDate);
		const endDate = startDate.add({milliseconds: RecordDialog.DEFAULT_DURATION});
		this.setEndDateTimeFields(endDate);

		this.dom.find(".duration").val(formatDuration(RecordDialog.DEFAULT_DURATION));
		this.dom.find(".startPositionId").val("");
		this.dom.find(".endPositionId").val("");
		this.dialog.toggleCheckbox(true);

		this.showOrHideTimeZoneFields();
		this.configureDateTimeFieldsForUser();
		this.validateDateTimeChangeableUntil();
	}

	public edit(recording: T) {
		this.originalRecording = recording;
		this.setDialogMode(DialogMode.Edit);
		this.dialog.toggleCheckbox(false);
		this.removeAllFeedback();
		this.disableFields(false);
		this.showButtons(true, this.isDeletable(recording), true);
		this.showRecording(recording);
		this.showDialog();
	}

	public duplicate(recording: T) {
		this.originalRecording = null;
		this.setDialogMode(DialogMode.Duplicate);
		this.dialog.toggleCheckbox(false);
		this.removeAllFeedback();
		this.disableFields(false);
		if (this.config.userSelectionEnabled) {
			this.reloadUserList();
		}
		this.showButtons(true, false, true);
		this.showRecording(recording, true);
		this.updateDuration();
		this.showDialog();
	}

	public show(recording: T) {
		this.originalRecording = recording;
		this.setDialogMode(DialogMode.Show);
		this.dialog.toggleCheckbox(false);
		this.removeAllFeedback();
		this.showButtons(false, false, true);
		this.showRecording(recording);
		this.disableFields(true);
		this.showDialog();
	}

	/**
	 * Sets start and end date of dialog and updates the duration update fields that depend on this update
	 * @param start date
	 * @param end date
	 */
	public setStartEndTime(start: Temporal.ZonedDateTime | null, end: Temporal.ZonedDateTime | null) {
		if (start) {
			this.setStartDateTimeFields(start);
		}
		if (end) {
			this.setEndDateTimeFields(end);
		}
		/* update duration, date time changeable warning and time zone as these depend on start or end date */
		this.updateDuration();
		this.validateDateTimeChangeableUntil();
		this.showOrHideTimeZoneFields();
	}

	/**
	 * Clears the selected group selection and the variable {@code selectedGroupId}
	 * This applies if a user should be preselected when in an add/create dialog, regardless of the previous status of the dialog
	 * Otherwise, the dialog's user dropdown may not contain the desired user due to the selected group (TIMR-8199)
	 */
	public clearGroup(): void {
		if (this.groupDropdown) {
			this.groupDropdown.setSelection(null);
			this.selectedGroupId = null;
		}
	}

	protected initDialog() {
		this.dialog = new BootstrapModal.RecordModal({
			size: this.config.size,
			title: this.config.title ? this.config.title[this.dialogMode] : undefined,
			message: this.dom,
			css: {
				content: "border-NOT_INITIALIZED"
			},
			buttons: [{
				text: "dialog.delete",
				action: () => {
					this._delete();
				},
				css: "deleteButton btn-outline-danger",
				left: true
			}, {
				text: "dialog.save",
				action: () => {
					this.save();
				},
				css: "saveButton btn-primary"
			}, {
				text: "dialog.cancel",
				action: () => {
					this.closeDialog();
				},
				css: "cancelButton btn-default"
			}],
			checkbox: true,
			onOpen: () => this.onDialogOpen(),
			onClose: () => {
				this.dialog.setCheckboxValue(false);
			},
			preventCloseIfLightboxOpen: true
		});
	}

	protected save() {
		// abstract method
	}

	protected saveRecordDone(lastRecord?: Partial<T>) {
		if (this.dialogMode === DialogMode.Create) {
			Notify.success(messages["recording.added"]);
			this.onRecordCreated();
			if (lastRecord) {
				this.create(lastRecord, true);
				return;
			}
		} else {
			this.onRecordEdited();
		}
		this.closeDialog();
	}

	protected saveFailed() {
		this.dialog.resetLoadingButtons();
	}

	protected _delete() {
		// abstract method
	}

	protected deleteCancelled() {
		this.dialog.resetLoadingButtons();
	}

	protected deleteDone() {
		this.onRecordDeleted();
		this.closeDialog();
	}

	protected parseStartDateTime() {
		return parseDateTimeZone(
			this.dom.find(".startDate").val() as string,
			this.dom.find(".startTime").val() as string,
			this.dom.find(".startTimeZone").val() as string
		);
	}

	protected parseEndDateTime() {
		return parseDateTimeZone(
			this.dom.find(".endDate").val() as string,
			this.dom.find(".endTime").val() as string,
			this.dom.find(".endTimeZone").val() as string
		);
	}

	protected updateDuration() {
		const start = this.parseStartDateTime();
		const end = this.parseEndDateTime();
		const breakTime = parseIntegerNumber(this.dom.find(".breakTime").val() as string);
		const duration = Temporal.Duration.from({minutes: parseDuration(this.dom.find(".duration").val() as string)});
		const calculatedDuration = this.calculateDuration(start, end, breakTime);
		this.dom.find(".duration").val(formatDuration(calculatedDuration));

		// validate permission for date time changeable if duration has changed
		if (Temporal.Duration.compare(duration, calculatedDuration) !== 0) {
			this.validateDateTimeChangeableUntil();
		}
	}

	protected updateStartDateTime() {
		const endDateTime = this.parseEndDateTime();
		const breakTime = parseIntegerNumber(this.dom.find(".breakTime").val() as string);
		const startTimeZone = this.dom.find(".startTimeZone").val() as string;
		if (endDateTime) {
			// Calculate start exactly once from end - duration - break.
			const duration = Temporal.Duration.from({minutes: parseDuration(this.dom.find(".duration").val() as string)});
			const startDateTime = this.calculateStartDateTime(endDateTime, duration, startTimeZone, breakTime);
			const adjustedStartDateTime = this.asLocalDateTime(startDateTime);
			// Keep the calculated instant so the duration stays exact; in create/duplicate only swap the
			// display offset to the browser-local one (asLocalDateTime supplies it, and returns null in
			// other modes, where the source offset is kept).
			const effectiveStartDateTime = adjustedStartDateTime !== null
				? startDateTime.toInstant().toZonedDateTimeISO(adjustedStartDateTime.offset)
				: startDateTime;
			this.updateTimeZoneField("start", effectiveStartDateTime);
			this.dom.find(".startDate").val(formatDate(effectiveStartDateTime));
			this.dom.find(".startTime").val(formatTime(effectiveStartDateTime));
		}
		this.validateDateTimeChangeableUntil();
	}

	protected calculateStartDateTime(endDateTime: Temporal.ZonedDateTime, duration: Temporal.Duration, startTimeZone: string, breakTime: number): Temporal.ZonedDateTime {
		return endDateTime
			.subtract(duration)
			.subtract({minutes: breakTime})
			.toInstant()
			.toZonedDateTimeISO(startTimeZone);
	}

	protected updateEndDateTime() {
		const startDateTime = this.parseStartDateTime();
		const breakTime = parseIntegerNumber(this.dom.find(".breakTime").val() as string);
		const endTimeZone = this.dom.find(".endTimeZone").val() as string;
		if (startDateTime) {
			// Calculate end exactly once from start + duration + break.
			const duration = Temporal.Duration.from({minutes: parseDuration(this.dom.find(".duration").val() as string)});
			const endDateTime = this.calculateEndDateTime(startDateTime, duration, endTimeZone, breakTime);
			const adjustedEndDateTime = this.asLocalDateTime(endDateTime);
			// Keep the calculated instant so the duration stays exact; in create/duplicate only swap the
			// display offset to the browser-local one (asLocalDateTime supplies it, and returns null in
			// other modes, where the source offset is kept).
			const effectiveEndDateTime = adjustedEndDateTime !== null
				? endDateTime.toInstant().toZonedDateTimeISO(adjustedEndDateTime.offset)
				: endDateTime;
			this.updateTimeZoneField("end", effectiveEndDateTime);
			this.dom.find(".endDate").val(formatDate(effectiveEndDateTime));
			this.dom.find(".endTime").val(formatTime(effectiveEndDateTime));
		}
		this.validateDateTimeChangeableUntil();
	}

	/**
	 * Checks whether the criteria for the date of an entry are met and whether the entry may be saved or a warning is displayed.
	 * Currently there are only validations DateTimeChangeableType if the configuration is BY_USER_DAY_RANGE
	 */
	protected validateDateTimeChangeableUntil(forceChangeableAllowed: boolean = false) {
		if (this.config.dateTimeChangeableByUserDayRangeUntil !== null) {
			this.updateDateTimeChangeableWarning(forceChangeableAllowed);
		}
	}

	/**
	 * Returns the result whether the change of the date and time is allowed
	 * @private
	 */
	private getDateTimeChangeableResult(forceAllowed: boolean = false): DateTimeChangeableResult {
		// if forced or the editing user is multi user/team leader -> always allowed
		if (forceAllowed || permissionForSaveButton(this.config.userSelectionEnabled, this.getSelectedUserId(), this.config.isGroupLeaderForThemself)) {
			return "ALLOWED";
		}

		// end time is in the future -> denied
		const endDtForFutureCheck = this.parseEndDateTime();
		if (endDtForFutureCheck && Temporal.PlainDate.compare(endDtForFutureCheck.toPlainDate(), Temporal.Now.plainDateISO()) > 0) {
			return "NOT_ALLOWED_FUTURE";
		}

		// original or new start time is before the allowed range -> denied or warning, based on configured mode
		if (this.originalRecording && isBeforeDay(parseISOZonedDateTime(this.originalRecording.start), this.config.dateTimeChangeableByUserDayRangeUntil) ||
			isBeforeDay(this.parseStartDateTime(), this.config.dateTimeChangeableByUserDayRangeUntil)) {
			if (this.config.dateTimeChangeableByUserDayRangeMode === DateTimeChangeableByUserDayRangeMode.PREVENT_EDIT) {
				return "NOT_ALLOWED_PREVENT_EDIT";
			} else {
				return "ALLOWED_WARNING";
			}
		}

		return "ALLOWED";
	}

	/**
	 * Updates the ui of the dialog depending on whether the date time changeable criteria are met (currently only active if BY_USER_DAY_RANGE)
	 * There is an individualized text and style of the warning based on different states:
	 * - NOT_ALLOWED_PREVENT_EDIT: time is before the allowed time -> red danger warning, save is denied
	 * - NOT_ALLOWED_FUTURE: time is in the future -> red danger warning, save is denied
	 * - ALLOWED_WARNING: time is before the allowed time -> orange warning, save is allowed
	 * @param forceChangeableAllowed force the method to return the state "ALLOWED"
	 */
	protected updateDateTimeChangeableWarning(forceChangeableAllowed: boolean = false) {
		const result: DateTimeChangeableResult = this.getDateTimeChangeableResult(forceChangeableAllowed);

		const saveAllowed = result === "ALLOWED" || result === "ALLOWED_WARNING";
		const mode = result === "NOT_ALLOWED_FUTURE" ? "" : this.dialogMode === DialogMode.Edit ? ".edit" : ".add";
		this.dom.find(".dateRangeWarning")
			.text(messages[`dialog.warning.dateTimeChangeable.byUserDayRange${mode}.${result}`])
			.toggleClass("alert-warning", saveAllowed)
			.toggleClass("alert-danger", !saveAllowed)
			.toggle(result !== "ALLOWED");
		setTimeout(() => this.dialog.getButtons("saveButton").prop("disabled", !saveAllowed));
	}

	protected calculateEndDateTime(startDateTime: Temporal.ZonedDateTime, duration: Temporal.Duration, endTimeZone: string, breakTime: number): Temporal.ZonedDateTime {
		return startDateTime
			.add(duration)
			.add({minutes: breakTime})
			.toInstant()
			.toZonedDateTimeISO(endTimeZone);
	}

	protected onSelectedUserChanged(user: User) {
		this.configureDateTimeFieldsForUser(user?.id);
		this.validateDateTimeChangeableUntil();
	}

	protected abstract isDateTimeChangeAllowed(userId?: number): boolean;

	protected isDeletable(recording: T): boolean {
		return this.isDateTimeChangeAllowed(recording.userId);
	}

	protected configureDateTimeFields(enabled: boolean) {
		this.dom.find(`.startDate, .endDate, .startHalfDay, .endHalfDay, .startTime, .endTime, .startTimeZone, .endTimeZone,
			 	.focusOnStartDateField, .focusOnEndDateField, .duration`).prop("disabled", !enabled);
		this.updateBreakTimeFieldState(false, enabled);
	}

	protected updateBreakTimeFieldState(hideBreakTimeField = false, enabled = true): void {
		const breakTimeField = this.dom.find(".breakTime");
		if (breakTimeField.length === 0) {
			return;
		}

		const breakTimeFieldId = breakTimeField.attr("id");
		const breakTimeLabel = breakTimeFieldId ? this.dom.find(`label[for="${breakTimeFieldId}"]`) : $();
		const breakTimeInputGroup = breakTimeField.closest(".input-group");
		const isBookableBreaksMode = globals.breakTimeRecordingMode === BreakTimeRecordingMode.RECORD_WITH_POSITION;
		const breakTime = parseIntegerNumber(breakTimeField.val() as string);
		const visible = !hideBreakTimeField && (!isBookableBreaksMode || (breakTime > 0));

		breakTimeLabel.toggle(visible);
		breakTimeInputGroup.toggle(visible);
		breakTimeField.prop("disabled",
			!enabled ||
			!globals.manualBreaksAllowed ||
			isBookableBreaksMode && visible
		);
	}

	protected getSelectedUserId(): number | null {
		return this.userCombobox?.getSelectedItem()?.id ?? null;
	}

	protected showOrHideTimeZoneFields(): void {
		const showTimeZones = this.shouldShowTimeZoneFields();
		this.dom.find(".timeZoneField").toggle(showTimeZones);

		// adapt border radius for the - right outer - time elements if time zone is invisible (via css pseudo :last-child it's not possible because just visibility is toggled)
		this.dom.find(".startTime, .endTime").toggleClass("border-radius-right", !showTimeZones);
	}

	protected shouldShowTimeZoneFields(): boolean {
		const currentTimeZone = getCurrentISOOffset();
		return globals.showTimeZone ||
			this.dom.find(".startTimeZone").val() !== this.dom.find(".endTimeZone").val() ||
			(this.dialogMode === DialogMode.Create || this.dialogMode === DialogMode.Duplicate) &&
			(this.dom.find(".startTimeZone").val() !== currentTimeZone ||
				this.dom.find(".endTimeZone").val() !== currentTimeZone);
	}

	protected initCustomFields(): void {
		if (SecurityStatus.isCustomFieldsForRecordAllowed()) {
			initCustomFields(this.dom, this.config.customFieldDefinitions, this.customFieldComboboxes, this.hasCustomFieldCheckboxChangeListener);
		}
	}

	protected updateCustomFields(recording: T, definitions: CustomFieldDefinition[]) {
		if (SecurityStatus.isCustomFieldsForRecordAllowed()) {
			updateCustomFields(this.dom, recording, definitions, this.customFieldComboboxes, this.hasCustomFieldCheckboxChangeListener);
		}
	}

	protected showDialog() {
		this.dialog.show();
		// timeout before trigger autoExpand, to get the correct height value
		setTimeout(() => {
			this.dom.find(".description").change();
		}, 150);
	}

	protected closeDialog() {
		this.dialog.close();
	}

	protected showRecording(recording: T, duplicate = false) {
		this.updateUserCombobox(this.config.users);
		this.durationChanged = false;
		this.lastRecordEnd = parseISOZonedDateTime(recording.end);
		if (duplicate) {
			this.dom.find(".id").val("");
			this.dom.find(".version").val("0");
			this.dom.find(".changed").val("true");
			this.dom.find(".startPositionId").val("");
			this.dom.find(".endPositionId").val("");
		} else {
			this.dom.find(".id").val(String(recording.id));
			this.dom.find(".version").val(String(recording.version));
			this.dom.find(".changed").val(String(recording.changed));
			this.dom.find(".startPositionId").val(recording.startPosition?.id);
			this.dom.find(".endPositionId").val(recording.endPosition?.id);
		}
		this.dom.find(".status").val(this.getStatus(recording));
		this.userCombobox?.setDisabled(!duplicate);
		this.userCombobox?.setSelectedItem({id: recording.userId, fullname: recording.userName}, false, true);
		this.dom.find(".groupRow").hide();

		const start = parseISOZonedDateTime(recording.start);
		this.setStartDateTimeFields(start);
		this.dom.find(".startTimeZone").val(recording.startTimezone);
		const end = parseISOZonedDateTime(recording.end);
		this.setEndDateTimeFields(end);
		this.dom.find(".endTimeZone").val(recording.endTimezone);
		this.dom.find(".breakTime").val((recording.breakTime ?? 0).toString());
		this.showOrHideTimeZoneFields();
		this.dom.find(".duration").val(formatDuration(this.calculateDuration(start, end, recording.breakTime)));
		this.validateDateTimeChangeableUntil(!duplicate);
		this.configureDateTimeFieldsForUser();
	}

	protected calculateDuration(start: Temporal.ZonedDateTime | null, end: Temporal.ZonedDateTime | null, breakTime: number | null): Temporal.Duration {
		return start != null && end != null ? start.until(end).subtract({minutes: breakTime || 0}) : new Temporal.Duration();
	}

	protected onRecordEdited() {
		$(this).triggerHandler(RecordDialog.EVENT_RECORD_EDITED);
	}

	protected onRecordCreated() {
		$(this).triggerHandler(RecordDialog.EVENT_RECORD_CREATED);
	}

	protected onRecordDeleted() {
		$(this).triggerHandler(RecordDialog.EVENT_RECORD_DELETED);
	}

	private initCalendars() {
		this.dom.find(".startDate").on("apply.daterangepicker", () => {
			this.updateTimeZoneField("start", this.asLocalDateTime(this.parseStartDateTime()));
			this.updateEndDateTime();
		});
		this.dom.find(".endDate").on("apply.daterangepicker", () => {
			this.updateTimeZoneField("end", this.asLocalDateTime(this.parseEndDateTime()));
			if (this.durationChanged) {
				this.updateStartDateTime();
			} else {
				this.updateDuration();
			}
		});
	}

	/**
	 * In create/duplicate, re-anchors the wall-clock time to the browser time zone so the offset follows
	 * the selected local date (DST-aware) instead of the persisted/source record offset.
	 * Returns null in every other mode, signalling callers to keep the original value and offset.
	 */
	protected asLocalDateTime(value: Temporal.ZonedDateTime | null): Temporal.ZonedDateTime | null {
		if (this.dialogMode === DialogMode.Create || this.dialogMode === DialogMode.Duplicate) {
			return value?.toPlainDateTime().toZonedDateTime(Temporal.Now.timeZoneId()) ?? null;
		}
		return null;
	}

	protected updateTimeZoneField(startEnd: "start" | "end", value: Temporal.ZonedDateTime | null): void {
		if (value === null) {
			return;
		}
		this.dom.find(`.${startEnd}TimeZone`).val(value.offset);
		this.showOrHideTimeZoneFields();
	}

	private setDateTime(startEnd: string, value: Temporal.ZonedDateTime) {
		this.setTimeChanged();
		this.dom.find("." + startEnd + "Date").val(formatDate(value));
		this.dom.find("." + startEnd + "Time").val(formatTime(value));
		if (this.durationChanged) {
			if (startEnd === "start") {
				this.updateEndDateTime();
			} else {
				this.updateStartDateTime();
			}
		} else {
			this.updateDuration();
		}
	}

	private createTimeShiftButtons() {
		this.dom.find(".focusOnStartDateField").click(() => {
			this.dom.find(".startDate").focus();
		});

		this.dom.find(".focusOnEndDateField").click(() => {
			this.dom.find(".endDate").focus();
		});

		this.dom.find(".setStartToLastEnd").click(() => {
			if (this.lastRecordEnd) {
				this.setDateTime("start", this.lastRecordEnd);
			}
			startTimeDropdownMenu.hide();
		});

		const startTimeDropdownMenu = this.dom.find(".startTime").closest(".dropdown").find(".dropdown-menu");
		this.dom.find(".setStartToNow").click(() => {
			this.setDateTime("start", Temporal.Now.zonedDateTimeISO());
			startTimeDropdownMenu.hide();
		});

		const endTimeDropdownMenu = this.dom.find(".endTime").closest(".dropdown").find(".dropdown-menu");
		this.dom.find(".setEndToNow").click(() => {
			this.setDateTime("end", Temporal.Now.zonedDateTimeISO());
			endTimeDropdownMenu.hide();
		});

		this.dom.find(".startTime")
			.focus(() => {
				const now = Temporal.Now.zonedDateTimeISO();
				startTimeDropdownMenu.find(".setStartToNow > a > span").text(`(${formatTime(now)})`);
				if (this.lastRecordEnd) {
					const startDateTime = this.lastRecordEnd;
					const startDate = formatDate(startDateTime);
					let timeString = formatTime(startDateTime);
					if (formatDate(now) !== startDate) {
						timeString += ", " + startDate;
					}
					startTimeDropdownMenu.find(".setStartToLastEnd").removeClass("disabled");
					startTimeDropdownMenu.find(".setStartToLastEnd > a > span").text(`(${timeString})`);
				} else {
					startTimeDropdownMenu.find(".setStartToLastEnd").addClass("disabled");
					startTimeDropdownMenu.find(".setStartToLastEnd > a > span").text("");
				}
				startTimeDropdownMenu.show();
			}).keydown(e => {
			if (e.keyCode === 9) {
				startTimeDropdownMenu.hide();
			}
		}).focusout((eventObject) => {
			normalizeTimeInput($(eventObject.delegateTarget));
			if (this.durationChanged) {
				this.updateEndDateTime();
			} else {
				this.updateDuration();
			}
			if (!$(eventObject.relatedTarget).is("a.shift-time-entries")) {
				startTimeDropdownMenu.hide();
			}
		});

		this.dom.find(".endTime")
			.focus(() => {
				endTimeDropdownMenu.find(".setEndToNow > a > span").text(`(${formatTime(Temporal.Now.zonedDateTimeISO())})`);
				endTimeDropdownMenu.show();
			}).keydown(e => {
			if (e.keyCode === 9) {
				endTimeDropdownMenu.hide();
			}
		}).focusout((eventObject) => {
			normalizeTimeInput($(eventObject.delegateTarget));
			if (this.durationChanged) {
				this.updateStartDateTime();
			} else {
				this.updateDuration();
			}
			if (!$(eventObject.relatedTarget).is("a.shift-time-entries")) {
				endTimeDropdownMenu.hide();
			}
		});

		this.dom.find(".startDate").on("input", () => {
			this.updateTimeZoneField("start", this.asLocalDateTime(this.parseStartDateTime()));
			if (!this.durationChanged) {
				this.updateDuration();
			}
		});

		this.dom.find(".startDate").on("focusout", () => {
			if (this.durationChanged) {
				this.updateEndDateTime();
			}
		});

		this.dom.find(".endDate").on("input", () => {
			this.updateTimeZoneField("end", this.asLocalDateTime(this.parseEndDateTime()));
			if (this.durationChanged) {
				this.updateStartDateTime();
			} else {
				this.updateDuration();
			}
		});
		this.dom.find(".endTimeZone, .breakTime").on("input", () => {
			if (this.durationChanged) {
				this.updateStartDateTime();
			} else {
				this.updateDuration();
			}
		});

		this.dom.find(".duration").on("input", (eventObject) => {
			const startDateTime = this.parseStartDateTime();
			const breakTime = parseIntegerNumber(this.dom.find(".breakTime").val() as string);
			const endTimeZone = this.dom.find(".endTimeZone").val() as string;
			if (startDateTime) {
				const duration = Temporal.Duration.from({minutes: parseDuration($(eventObject.delegateTarget).val() as string)});
				const endDateTime = this.calculateEndDateTime(startDateTime, duration, endTimeZone, breakTime);
				this.dom.find(".endDate").val(formatDate(endDateTime));
				this.dom.find(".endTime").val(formatTime(endDateTime));
			}
			this.durationChanged = parseDuration($(eventObject.delegateTarget).val() as string) !== 0;
		}).on("focusout", () => {
			if (this.durationChanged) {
				this.validateDateTimeChangeableUntil();
			}
			this.updateDuration();
		});
		if (!this.isDateTimeChangeAllowed()) {
			this.disableFields(true);
		}

		this.dom.find("hidden").prop("tabindex", "-1");

		this.addChangeEventListeners();
	}

	private setTimeChanged(value = true) {
		this.dom.find(".changed").val(String(value));
	}

	private addChangeEventListeners() {
		this.dom.find("input:checkbox.checkbox").change(() => {
			const hiddenInput = this.dom.find(`input[name="${$(this).val() as string}"]`);
			if (hiddenInput) {
				hiddenInput.val($(this).is(":checked").toString());
			}
		});

		this.dom.find(".startDate, .startTime, .startTimeZone, .endDate, .endTime, .endTimeZone, .duration, .breakTime").on("focusout", () => {
			const start = parseISOZonedDateTime(this.originalRecording?.start);
			const end = parseISOZonedDateTime(this.originalRecording?.end);

			const changed = !(parseIntegerNumber(this.dom.find(".breakTime").val() as string) === parseIntegerNumber(String(this.originalRecording?.breakTime))
				&& this.dom.find(".startTime").val() === formatTime(start)
				&& this.dom.find(".startDate").val() === formatDate(start)
				&& this.dom.find(".startTimeZone").val() === (this.originalRecording?.startTimezone ?? "")
				&& this.dom.find(".endTime").val() === formatTime(end)
				&& this.dom.find(".endDate").val() === formatDate(end)
				&& this.dom.find(".endTimeZone").val() === (this.originalRecording?.endTimezone ?? ""));
			this.setTimeChanged(changed);
		});
	}

	private initDescriptionAutoExpand() {
		this.dom.find(".description").on("input change", e => {
			FormUtil.autoExpand(e.target);
		});
	}

	private initGroupDropdown() {
		const groupRow = this.dom.find(".groupRow");
		if (groupRow.length > 0) {
			this.groupDropdown = new GroupDropdown({
				rootElement: this.dom.find(".groupDropdown"),
				allowSelectionOfNotAssigned: false
			});
			$(this.groupDropdown).on(GroupDropdown.EVENT_SELECTED, (e, group: Group) => {
				this.selectedGroupId = group != null ? group.id : 0;
				this.reloadUserList();
			});
			groupRow.toggle(this.config.userSelectionEnabled);
		}
	}

	private initUserCombobox() {
		const userIdElement = this.dom.find(".userId");
		if (userIdElement.length > 0) {
			this.userCombobox = new Combobox<DisplayedUser>({
				items: this.config.users,
				formatter: (user) => {
					return user.fullname;
				},
				idElement: userIdElement,
				filterElement: this.dom.find(".userFilter"),
				caretElement: this.dom.find(".userCaret"),
				clearable: false
			});
			$(this.userCombobox).on(Combobox.EVENT_CHANGED, (e, user: User) => {
				this.onSelectedUserChanged(user);
			});
		}
	}

	private reloadUserList() {
		const data: { activeUsersOnly: boolean, groupId?: number } = {activeUsersOnly: true};
		if (this.selectedGroupId) {
			data.groupId = this.selectedGroupId;
		}
		api("reports/filterUsers.json").get(data).done((responseData) => {
			this.updateUserCombobox(responseData.users, this.getSelectedUserId());
		});
	}

	protected filterUsers(users: User[]): User[] {
		return $.grep(users, (user) => {
			return !user.disabled && this.isDateTimeChangeAllowed(user.id);
		});
	}

	protected updateUserCombobox(users: User[], selectedUserId: number = null) {
		const dialogUserRow = this.dom.find(".userRow");
		const filteredUsers = this.filterUsers(users);
		if (this.userCombobox != null) {
			this.userCombobox.updateItems(filteredUsers, null, true);
			this.userCombobox.setSelectedItemById(selectedUserId, true);
			dialogUserRow.toggle(this.config.userSelectionEnabled);
		} else {
			dialogUserRow.hide();
		}
	}

	protected configureDateTimeFieldsForUser(userId?: number) {
		this.configureDateTimeFields(this.isDateTimeChangeAllowed(userId || this.getSelectedUserId()));
	}

	private setDateTimeFields(field: string, date: Temporal.ZonedDateTime | null) {
		this.dom.find("." + field + "Date").val(formatDate(date));
		this.dom.find("." + field + "Time").val(formatTime(date));
		this.dom.find("." + field + "TimeZone").val(date?.offset ?? getCurrentISOOffset());
	}

	private setStartDateTimeFields(startDate: Temporal.ZonedDateTime | null): void {
		this.setDateTimeFields("start", startDate);
	}

	private setEndDateTimeFields(endDate: Temporal.ZonedDateTime | null): void {
		this.setDateTimeFields("end", endDate);
	}

	private showButtons(saveButton: boolean, deleteButton: boolean, cancelButton: boolean) {
		this.dialog.toggleButton("saveButton", saveButton);
		this.dialog.toggleButton("deleteButton", deleteButton);
		this.dialog.toggleButton("cancelButton", cancelButton);
	}

	private removeAllFeedback() {
		FormUtil.removeAllFeedback(this.dom);
	}

	protected disableFields(disabled: boolean) {
		const inputFields = this.dom.find("input, textarea, select, button");
		inputFields.prop("disabled", disabled);
		this.configureDateTimeFields(!disabled);
	}

	protected onDialogOpen() {
		this.updateBorder();
	}

	protected abstract updateBorder(): void;

	protected updateTitle() {
		if (this.config.title) {
			this.dialog.setTitle(this.config.title[this.dialogMode]);
		}
	}

	private setDialogMode(dialogMode: DialogMode) {
		this.dialogMode = dialogMode;
		this.updateTitle();
	}

	protected abstract getStatus(record: T): RecordedEntryStatus;
}
